/* LS - Events Calendar (card list inside a popup + Appointo booking)
   Shows a month's events as cards (image + colored band + details + Reserve).
   Filters: All / Milford / Powell (default All). In "All" cards are colored by
   location; in a single-location view they use the blue. "Reserve Your Spot"
   loads that event's Appointo booking via window.InitFromPandaBot(handle).
   Events flagged noReservation show a plain note in place of that button.
   An event carrying a `days` list repeats on those weekdays and is expanded into one
   card per matching date in the month being viewed, bounded by `date` / `endDate`. */

if (!customElements.get('events-calendar')) {
  customElements.define(
    'events-calendar',
    class EventsCalendar extends HTMLElement {
      connectedCallback() {
        this.modal = this.querySelector('[data-ec-modal]');
        this.openBtn = this.querySelector('[data-ec-open]');
        this.closeEls = this.querySelectorAll('[data-ec-close]');
        this.calendarView = this.querySelector('[data-ec-calendar]');
        this.bookingView = this.querySelector('[data-ec-booking]');
        this.bookingName = this.querySelector('[data-ec-booking-name]');
        this.cardsEl = this.querySelector('[data-ec-cards]');
        this.monthEl = this.querySelector('[data-ec-month]');
        this.emptyEl = this.querySelector('[data-ec-empty]');
        this.filterBtns = this.querySelectorAll('[data-ec-filter]');
        this.prevBtn = this.querySelector('[data-ec-prev]');
        this.nextBtn = this.querySelector('[data-ec-next]');
        this.backBtn = this.querySelector('[data-ec-back]');

        this.milfordLabel = this.dataset.ecMilfordLabel || 'Milford';
        this.powellLabel = this.dataset.ecPowellLabel || 'Powell';
        this.noReservationLabel = this.dataset.ecNoReservationLabel || 'No reservation needed';
        this.viewMoreLabel = this.dataset.ecViewMoreLabel || 'View more';
        this.viewLessLabel = this.dataset.ecViewLessLabel || 'View less';
        this.descLimit = parseInt(this.dataset.ecDescLimit, 10);
        if (isNaN(this.descLimit)) this.descLimit = 0;

        const parsed = this._parseEvents();
        this.events = parsed.events;
        this.repeating = parsed.repeating;
        this.filter = 'all';

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        this.today = today;

        const start = this._openingDate(today);
        this.viewYear = start.getFullYear();
        this.viewMonth = start.getMonth();

        this.open = this.open.bind(this);
        this.close = this.close.bind(this);
        this._onKey = this._onKey.bind(this);
        this._onDocClick = this._onDocClick.bind(this);
        this._onCardClick = this._onCardClick.bind(this);

        this.triggerHash = (this.dataset.ecTrigger || '').trim().toLowerCase();
        if (this.triggerHash && this.triggerHash.charAt(0) !== '#') {
          this.triggerHash = '#' + this.triggerHash;
        }

        if (this.openBtn) this.openBtn.addEventListener('click', this.open);
        this.closeEls.forEach((el) => el.addEventListener('click', this.close));
        this.filterBtns.forEach((b) =>
          b.addEventListener('click', () => this.setFilter(b.dataset.ecFilter))
        );
        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.shiftMonth(-1));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.shiftMonth(1));
        if (this.backBtn) this.backBtn.addEventListener('click', () => this._showCalendar());
        if (this.cardsEl) this.cardsEl.addEventListener('click', this._onCardClick);

        if (this.triggerHash) document.addEventListener('click', this._onDocClick);
        window.addEventListener('ls:open-events-calendar', this.open);

        this.render();
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this._onKey);
        document.removeEventListener('click', this._onDocClick);
        window.removeEventListener('ls:open-events-calendar', this.open);
        if (this._obs) this._obs.disconnect();
        if (this._fallback) clearTimeout(this._fallback);
        this._unlock();
      }

      _parseEvents() {
        const el = this.querySelector('[data-ec-events]');
        let raw = [];
        try {
          raw = JSON.parse((el && el.textContent.trim()) || '[]');
        } catch (err) {
          raw = [];
        }
        const events = [];
        const repeating = [];
        raw.forEach((e) => {
          const dows = this._parseDays(e.days);
          if (dows.length) {
            repeating.push(
              Object.assign({}, e, { _dows: dows, _start: this._parseDate(e.date), _end: this._parseDate(e.endDate) })
            );
            return;
          }
          const d = this._parseDate(e.date);
          if (d) events.push(Object.assign({}, e, { _d: d }));
        });
        return { events: events, repeating: repeating };
      }

      _parseDate(value) {
        const p = String(value || '').split('-');
        if (p.length !== 3) return null;
        const d = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
        if (isNaN(d.getTime())) return null;
        d.setHours(0, 0, 0, 0);
        return d;
      }

      // Accepts a metafield list or a comma-separated string, full names or abbreviations.
      _parseDays(value) {
        const DOW3 = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
        const list = Array.isArray(value) ? value : String(value || '').split(',');
        const out = [];
        list.forEach((name) => {
          const i = DOW3.indexOf(String(name).trim().slice(0, 3).toLowerCase());
          if (i > -1 && out.indexOf(i) === -1) out.push(i);
        });
        return out;
      }

      // The month to open on: this one if anything repeats now, else the next event's.
      _openingDate(today) {
        const live = this.repeating.some(
          (r) => (!r._start || r._start <= today) && (!r._end || r._end >= today)
        );
        if (live) return today;
        const soonest = this.events
          .map((e) => e._d)
          .concat(this.repeating.map((r) => r._start).filter((d) => d && (d > today)))
          .filter((d) => d >= today)
          .sort((a, b) => a - b);
        return soonest.length ? soonest[0] : today;
      }

      // One occurrence per matching weekday in the month, inside the event's date bounds.
      _occurrences(r, year, month) {
        const out = [];
        const lastDay = new Date(year, month + 1, 0).getDate();
        for (let day = 1; day <= lastDay; day++) {
          const d = new Date(year, month, day);
          d.setHours(0, 0, 0, 0);
          if (r._dows.indexOf(d.getDay()) === -1) continue;
          if (r._start && d < r._start) continue;
          if (r._end && d > r._end) continue;
          out.push(Object.assign({}, r, { _d: d }));
        }
        return out;
      }

      setFilter(f) {
        if (f !== 'all' && f !== 'milford' && f !== 'powell') return;
        this.filter = f;
        this.filterBtns.forEach((b) => {
          const active = b.dataset.ecFilter === f;
          b.classList.toggle('is-active', active);
          b.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        this.render();
      }

      shiftMonth(delta) {
        this.viewMonth += delta;
        if (this.viewMonth < 0) { this.viewMonth = 11; this.viewYear--; }
        if (this.viewMonth > 11) { this.viewMonth = 0; this.viewYear++; }
        this.render();
      }

      _monthEvents() {
        let list = this.events.filter(
          (e) => e._d.getFullYear() === this.viewYear && e._d.getMonth() === this.viewMonth
        );
        this.repeating.forEach((r) => {
          list = list.concat(this._occurrences(r, this.viewYear, this.viewMonth));
        });
        if (this.filter !== 'all') list = list.filter((e) => e.location === this.filter);
        return list.sort((a, b) => a._d - b._d);
      }

      render() {
        const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        if (this.monthEl) this.monthEl.textContent = MONTHS[this.viewMonth] + ' ' + this.viewYear;

        const monthEvents = this._monthEvents();

        this.rendered = monthEvents;

        let html = '';
        monthEvents.forEach((e, i) => {
          const loc = e.location || 'other';
          const locLabel = loc === 'milford' ? this.milfordLabel : loc === 'powell' ? this.powellLabel : loc;
          const meta = locLabel + ' · ' + DOW[e._d.getDay()] + ', ' + MON[e._d.getMonth()] + ' ' + e._d.getDate();
          const media = e.image
            ? '<img class="ls-ec__card-img" src="' + this._attr(e.image) + '" alt="' + this._attr(e.title) + '" loading="lazy">'
            : '<div class="ls-ec__card-ph">Event photo</div>';

          html +=
            '<article class="ls-ec__card" data-ec-index="' + i + '">' +
            '<div class="ls-ec__card-media">' + media + '</div>' +
            '<div class="ls-ec__card-body">' +
            '<p class="ls-ec__card-meta">' + this._esc(meta) + '</p>' +
            '<h3 class="ls-ec__card-title">' + this._esc(e.title) + '</h3>' +
            (e.time ? '<p class="ls-ec__card-time">' + this._esc(e.time) + '</p>' : '') +
            this._descHtml(e.description) +
            (e.noReservation
              ? '<p class="ls-ec__card-note">' + this._esc(this.noReservationLabel) + '</p>'
              : '<button type="button" class="ls-ec__card-btn" data-ec-handle="' + this._attr(e.handle) + '" data-ec-name="' + this._attr(e.title) + '">Reserve Your Spot</button>') +
            '</div>' +
            '</article>';
        });

        if (this.cardsEl) this.cardsEl.innerHTML = html;
        if (this.emptyEl) this.emptyEl.hidden = monthEvents.length > 0;
      }

      // Collapsed by default past the character limit; 0 means never shorten.
      _descHtml(text) {
        const full = String(text == null ? '' : text);
        if (!full) return '';
        if (!this.descLimit || full.length <= this.descLimit) {
          return '<p class="ls-ec__card-desc">' + this._esc(full) + '</p>';
        }
        return (
          '<p class="ls-ec__card-desc" data-ec-desc>' + this._esc(this._shorten(full)) + '</p>' +
          '<button type="button" class="ls-ec__card-more" data-ec-more aria-expanded="false">' +
          this._esc(this.viewMoreLabel) +
          '</button>'
        );
      }

      _shorten(text) {
        let cut = text.slice(0, this.descLimit);
        const lastSpace = cut.lastIndexOf(' ');
        // Only back off to a word boundary if one sits reasonably near the end.
        if (lastSpace > this.descLimit * 0.6) cut = cut.slice(0, lastSpace);
        return cut.replace(/[\s.,;:!?-]+$/, '') + '\u2026';
      }

      _toggleDesc(btn) {
        const card = btn.closest('.ls-ec__card');
        const p = card && card.querySelector('[data-ec-desc]');
        const ev = card && this.rendered[Number(card.dataset.ecIndex)];
        if (!p || !ev) return;
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        p.textContent = expanded ? this._shorten(String(ev.description)) : String(ev.description);
        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        btn.textContent = expanded ? this.viewMoreLabel : this.viewLessLabel;
      }

      _onCardClick(e) {
        const more = e.target.closest('[data-ec-more]');
        if (more) {
          this._toggleDesc(more);
          return;
        }
        const btn = e.target.closest('[data-ec-handle]');
        if (!btn) return;
        this.book(btn.getAttribute('data-ec-handle'), btn.getAttribute('data-ec-name'));
      }

      book(handle) {
        if (!handle) return;
        if (typeof window.InitFromPandaBot !== 'function') {
          console.error('InitFromPandaBot not found — the Appointo app-embed script is not loaded.');
          return;
        }
        // Show our own loading state (not the cards) so nothing distracting sits behind
        // the app, and there's no empty gap while the app loads.
        this._showBooking();
        window.InitFromPandaBot(handle);
        // Hide our popup only once the app's booking widget has actually appeared.
        this._obs = new MutationObserver((mutations) => {
          for (const m of mutations) {
            for (const node of m.addedNodes) {
              if (node.nodeType !== 1) continue;
              const id = (node.id || '').toLowerCase();
              const cls = (typeof node.className === 'string' ? node.className : '').toLowerCase();
              const matches =
                node.tagName === 'IFRAME' ||
                id.indexOf('appointo') > -1 || cls.indexOf('appointo') > -1 ||
                id.indexOf('pandabot') > -1 || cls.indexOf('pandabot') > -1 ||
                (node.querySelector && node.querySelector('iframe, [class*="appointo"], [id*="appointo"], [class*="pandabot"], [id*="pandabot"]'));
              if (matches) { this.close(); return; }
            }
          }
        });
        this._obs.observe(document.body, { childList: true, subtree: true });
        // Fallback: if the widget's markup isn't detected, close once it's surely open.
        this._fallback = setTimeout(() => this.close(), 5000);
      }

      _showBooking() {
        if (this.calendarView) this.calendarView.hidden = true;
        if (this.bookingView) this.bookingView.hidden = false;
      }

      _showCalendar() {
        if (this._t) { clearTimeout(this._t); this._t = null; }
        if (this.bookingView) this.bookingView.hidden = true;
        if (this.calendarView) this.calendarView.hidden = false;
      }

      open() {
        if (!this.modal) return;
        this._showCalendar();
        this.modal.hidden = false;
        requestAnimationFrame(() => this.modal.classList.add('is-open'));
        this._lock();
        document.addEventListener('keydown', this._onKey);
        const dialog = this.querySelector('.ls-ec__dialog');
        if (dialog) dialog.focus();
      }

      close() {
        if (!this.modal) return;
        if (this._obs) { this._obs.disconnect(); this._obs = null; }
        if (this._fallback) { clearTimeout(this._fallback); this._fallback = null; }
        this.modal.classList.remove('is-open');
        this.modal.hidden = true;
        this._unlock();
        document.removeEventListener('keydown', this._onKey);
        if (this.openBtn) this.openBtn.focus();
      }

      _onKey(e) { if (e.key === 'Escape') this.close(); }

      _onDocClick(e) {
        const link = e.target.closest('a[href]');
        if (!link) return;
        if ((link.hash || '').toLowerCase() === this.triggerHash) {
          e.preventDefault();
          this.open();
        }
      }

      _lock() { this._scrollY = window.scrollY; document.body.style.overflow = 'hidden'; }
      _unlock() { document.body.style.overflow = ''; }

      _esc(s) {
        return (s == null ? '' : String(s)).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
      }
      _attr(s) { return (s == null ? '' : String(s)).replace(/"/g, '&quot;'); }
    }
  );
}
